import { useEffect, useMemo, useRef, useState } from "react";
import { FaLock, FaPiggyBank, FaShieldAlt } from "react-icons/fa";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FormGrid, FieldInput, FieldTextarea, FieldSelect, FieldColor, FieldSwitch } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { SAVINGS_ACCOUNT_TYPES, SAVINGS_CURRENCIES } from "../_UTILS/savings.util";

const EMPTY = {
    name: "", kind: "BANK", institution: "", holderName: "", storageLocation: "", currency: "ARS",
    openingBalance: "0", annualRate: "", color: "#e83a52", notes: "", isActive: true,
    alias: "", cbuCvu: "", accountNumber: "", username: "", accessPin: "", currentAccessPin: "", newAccessPin: "",
    clearSecureData: false, clearAccessPin: false,
};

const FIELD_ORDER = [
    "name", "kind", "institution", "holderName", "storageLocation", "currency", "openingBalance", "annualRate", "color", "notes",
    "alias", "cbuCvu", "accountNumber", "username", "accessPin", "newAccessPin", "currentAccessPin",
];

const valuesFrom = (account) => account ? {
    ...EMPTY,
    name: account.name ?? "", kind: account.kind ?? "BANK", institution: account.institution ?? "",
    holderName: account.holderName ?? "", storageLocation: account.storageLocation ?? "", currency: account.currency ?? "ARS",
    openingBalance: String(account.openingBalance ?? 0), annualRate: account.annualRate == null ? "" : String(account.annualRate),
    color: account.color ?? "#e83a52", notes: account.notes ?? "", isActive: account.isActive !== false,
} : EMPTY;

const hasMoreDecimals = (value, max) => {
    const text = String(value ?? "").trim();
    const decimal = text.split(".")[1];
    return !!decimal && decimal.length > max;
};

const onValidate = ({ values, edit, secureTouched, needsCurrentPin, needsNewPinForSecureData }) => {
    const errors = {};
    const name = values.name.trim();
    const cbu = values.cbuCvu.trim();

    if (!name) errors.name = "Ingresá un nombre para identificar la cuenta.";
    else if (name.length > 160) errors.name = "El nombre no puede superar 160 caracteres.";

    if (values.institution.trim().length > 160) errors.institution = "La institución no puede superar 160 caracteres.";
    if (values.holderName.trim().length > 180) errors.holderName = "El titular no puede superar 180 caracteres.";
    if (values.storageLocation.trim().length > 220) errors.storageLocation = "La ubicación no puede superar 220 caracteres.";

    const openingBalance = Number(values.openingBalance);
    if (!Number.isFinite(openingBalance)) errors.openingBalance = "Ingresá un saldo inicial válido.";
    else if (hasMoreDecimals(values.openingBalance, 2)) errors.openingBalance = "El saldo admite hasta 2 decimales.";

    if (values.annualRate !== "") {
        const annualRate = Number(values.annualRate);
        if (!Number.isFinite(annualRate)) errors.annualRate = "Ingresá una tasa válida o dejala vacía.";
        else if (annualRate < -999.9999 || annualRate > 999.9999) errors.annualRate = "La tasa debe estar entre -999,9999% y 999,9999%.";
        else if (hasMoreDecimals(values.annualRate, 4)) errors.annualRate = "La tasa admite hasta 4 decimales.";
    }

    if (values.notes.length > 5000) errors.notes = "Las notas no pueden superar 5000 caracteres.";
    if (values.alias.trim().length > 180) errors.alias = "El alias no puede superar 180 caracteres.";
    if (cbu && !/^\d{22}$/.test(cbu)) errors.cbuCvu = "El CBU / CVU debe tener exactamente 22 dígitos, sin espacios ni guiones.";
    if (values.accountNumber.trim().length > 100) errors.accountNumber = "El número de cuenta no puede superar 100 caracteres.";
    if (values.username.trim().length > 180) errors.username = "El usuario no puede superar 180 caracteres.";

    if (!edit && secureTouched && !values.accessPin) errors.accessPin = "Definí un PIN para proteger los datos sensibles.";
    if (!edit && values.accessPin && (values.accessPin.length < 4 || values.accessPin.length > 32)) errors.accessPin = "El PIN debe tener entre 4 y 32 caracteres.";
    if (edit && values.newAccessPin && (values.newAccessPin.length < 4 || values.newAccessPin.length > 32)) errors.newAccessPin = "El nuevo PIN debe tener entre 4 y 32 caracteres.";
    if (needsNewPinForSecureData && values.newAccessPin.length < 4) errors.newAccessPin = "Definí un nuevo PIN para proteger los datos sensibles.";
    if (needsCurrentPin && (values.currentAccessPin.length < 4 || values.currentAccessPin.length > 32)) errors.currentAccessPin = "Ingresá el PIN actual para autorizar este cambio.";

    return errors;
};

export const SavingsAccountModal = ({ isOpen, onClose, account = null, onSave, isLoading = false }) => {
    const [values, setValues] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const formRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        setValues(valuesFrom(account));
        setErrors({});
    }, [isOpen, account]);

    const edit = !!account?.id;
    const secureTouched = !!(values.alias || values.cbuCvu || values.accountNumber || values.username || values.clearSecureData);
    const pinTouched = !!(values.newAccessPin || values.clearAccessPin);
    const needsCurrentPin = edit && !!account?.requiresPin && (secureTouched || pinTouched);
    const needsNewPinForSecureData = edit && !account?.requiresPin && secureTouched && !values.clearSecureData;
    const dirty = useMemo(() => JSON.stringify(values) !== JSON.stringify(valuesFrom(account)), [values, account]);

    const clearFieldError = (name) => setErrors((current) => {
        if (!current[name]) return current;
        const next = { ...current };
        delete next[name];
        return next;
    });

    const field = (name) => ({
        id: `savings_account_${name}`, name,
        value: values[name] ?? "",
        error: errors[name] || null,
        onChange: (event) => {
            clearFieldError(name);
            setValues((current) => ({ ...current, [name]: event.target.value }));
        },
    });

    const focusField = (name) => {
        if (!name) return;
        window.requestAnimationFrame(() => {
            const root = formRef.current;
            if (!root) return;
            const candidates = [
                ...root.querySelectorAll(`[data-field="${name}"]`),
                ...root.querySelectorAll(`[id^="savings_account_${name}_"]`),
            ];
            const target = candidates.find((node) => !node.disabled && node.getAttribute("aria-hidden") !== "true") || candidates[0];
            if (!target) return;
            const shell = target.closest(".LStudio__app_field") || target;
            shell.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
            window.setTimeout(() => target.focus?.({ preventScroll: true }), 220);
        });
    };

    const applyErrors = (nextErrors = {}) => {
        const normalized = Object.fromEntries(Object.entries(nextErrors || {}).filter(([, message]) => !!message));
        setErrors(normalized);
        const first = FIELD_ORDER.find((name) => normalized[name]) || Object.keys(normalized)[0];
        if (first) focusField(first);
        return first;
    };

    const submit = async () => {
        const localErrors = onValidate({ values, edit, secureTouched, needsCurrentPin, needsNewPinForSecureData });
        if (Object.keys(localErrors).length) {
            applyErrors(localErrors);
            return;
        }

        const payload = {
            name: values.name.trim(), kind: values.kind, institution: values.institution.trim() || null,
            holderName: values.holderName.trim() || null, storageLocation: values.storageLocation.trim() || null,
            currency: values.currency, openingBalance: Number(values.openingBalance || 0),
            annualRate: values.annualRate === "" ? null : Number(values.annualRate), color: values.color || null,
            notes: values.notes.trim() || null, isActive: !!values.isActive,
        };

        if (!edit) {
            if (values.alias.trim()) payload.alias = values.alias.trim();
            if (values.cbuCvu.trim()) payload.cbuCvu = values.cbuCvu.trim();
            if (values.accountNumber.trim()) payload.accountNumber = values.accountNumber.trim();
            if (values.username.trim()) payload.username = values.username.trim();
            if (values.accessPin) payload.accessPin = values.accessPin;
        } else {
            if (values.clearSecureData) {
                payload.alias = null; payload.cbuCvu = null; payload.accountNumber = null; payload.username = null;
            } else {
                if (values.alias.trim()) payload.alias = values.alias.trim();
                if (values.cbuCvu.trim()) payload.cbuCvu = values.cbuCvu.trim();
                if (values.accountNumber.trim()) payload.accountNumber = values.accountNumber.trim();
                if (values.username.trim()) payload.username = values.username.trim();
            }
            if (values.currentAccessPin) payload.currentAccessPin = values.currentAccessPin;
            if (values.newAccessPin) payload.newAccessPin = values.newAccessPin;
            if (values.clearAccessPin) payload.clearAccessPin = true;
        }

        const result = await onSave?.(payload);
        if (result === false) return;
        if (result && typeof result === "object" && result.success === false) {
            const serverErrors = result.fields || result.errors || {};
            if (serverErrors && Object.keys(serverErrors).length) applyErrors(serverErrors);
            return;
        }
        onClose?.();
    };

    return (
        <AppModal isOpen={isOpen} onClose={onClose} size="xl" isDirty={dirty}
            title={edit ? "Editar cuenta" : "Nueva cuenta / lugar de ahorro"}
            description="Registra dónde está tu dinero, saldo inicial, tasa de referencia y datos de acceso protegidos."
            footer={<><AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton><AppButton onClick={submit} isLoading={isLoading}>{edit ? "Guardar cambios" : "Crear cuenta"}</AppButton></>}>
            <div ref={formRef} className="LStudio__savings_modal_stack">
                <div className="LStudio__savings_helpbox"><FaPiggyBank /><div><strong>Saldo real por movimientos</strong><p>El saldo actual se calcula con el saldo inicial + ingresos + rendimientos - gastos + transferencias. No tenés que actualizarlo a mano.</p></div></div>
                <FormGrid columns={2}>
                    <FieldInput {...field("name")} label="Nombre" placeholder="Mercado Pago / Galicia / Caja fuerte" isRequired maxLength={160} />
                    <FieldSelect {...field("kind")} label="Tipo" options={SAVINGS_ACCOUNT_TYPES} isSearchable />
                    <FieldInput {...field("institution")} label="Banco / institución" placeholder="Banco Galicia" maxLength={160} />
                    <FieldInput {...field("holderName")} label="Titular" placeholder="Nombre del titular" maxLength={180} />
                    <FieldInput {...field("storageLocation")} label="Dónde está guardado" placeholder="App, broker, caja, efectivo..." maxLength={220} />
                    <FieldSelect {...field("currency")} label="Moneda" options={SAVINGS_CURRENCIES} isSearchable />
                    <FieldInput {...field("openingBalance")} type="number" step="0.01" label="Saldo inicial" placeholder="0.00" />
                    <FieldInput {...field("annualRate")} type="number" step="0.0001" min="-999.9999" max="999.9999" label="Tasa anual de referencia (%)" placeholder="Ej: 32.5" hint="Informativa. El rendimiento real se registra como movimiento." />
                    <FieldColor {...field("color")} label="Color identificador" showSwatches={false} />
                    <FieldSwitch id="savings_account_active" label="Estado" text="Cuenta activa" isChecked={!!values.isActive} onChange={(event) => setValues((current) => ({ ...current, isActive: event.target.checked }))} />
                    <FieldTextarea {...field("notes")} label="Notas" placeholder="Condiciones, objetivo de esta cuenta, observaciones..." rows={3} maxLength={5000} span={2} />
                </FormGrid>

                <div className="LStudio__savings_secure_section">
                    <div className="LStudio__savings_secure_title"><FaShieldAlt /><div><strong>Datos protegidos</strong><small>AES-256-GCM en base de datos + PIN con bcrypt para revelarlos.</small></div></div>
                    {edit && account?.requiresPin && <p className="LStudio__savings_secure_notice"><FaLock /> Los campos que ya existen no se muestran al editar. Dejalos vacíos para conservarlos.</p>}
                    <FormGrid columns={2}>
                        <FieldInput {...field("alias")} label="Alias" placeholder={edit && account?.secureFields?.alias ? "Guardado · escribir para reemplazar" : "mi.alias.banco"} autoComplete="off" maxLength={180} />
                        <FieldInput {...field("cbuCvu")} label="CBU / CVU" placeholder={edit && account?.secureFields?.cbuCvu ? "Guardado · escribir para reemplazar" : "0000000000000000000000"} autoComplete="off" inputMode="numeric" maxLength={22} />
                        <FieldInput {...field("accountNumber")} label="Número de cuenta" placeholder={edit && account?.secureFields?.accountNumber ? "Guardado · escribir para reemplazar" : "Cuenta / comitente"} autoComplete="off" maxLength={100} />
                        <FieldInput {...field("username")} label="Usuario de acceso" placeholder={edit && account?.secureFields?.username ? "Guardado · escribir para reemplazar" : "usuario / email"} autoComplete="off" maxLength={180} />
                        {!edit && <FieldInput {...field("accessPin")} type="pin" label="PIN de protección" placeholder="Mínimo 4 caracteres" hint="Obligatorio si guardás datos sensibles." autoComplete="off" maxLength={32} />}
                        {edit && <FieldInput {...field("newAccessPin")} type="pin" label={needsNewPinForSecureData ? "Nuevo PIN *" : "Nuevo PIN"} placeholder={needsNewPinForSecureData ? "Obligatorio para proteger los datos nuevos" : "Dejar vacío para conservar"} hint="Entre 4 y 32 caracteres." autoComplete="off" maxLength={32} />}
                        {needsCurrentPin && <FieldInput {...field("currentAccessPin")} type="pin" label="PIN actual" placeholder="Necesario para cambiar datos protegidos" isRequired autoComplete="off" maxLength={32} />}
                        {edit && <FieldSwitch id="savings_clear_secure" label="Datos sensibles" text="Borrar alias, CBU/CVU, número y usuario" isChecked={!!values.clearSecureData} onChange={(event) => setValues((current) => ({ ...current, clearSecureData: event.target.checked }))} />}
                        {edit && <FieldSwitch id="savings_clear_pin" label="PIN" text="Quitar PIN (solo si no quedan datos sensibles)" isChecked={!!values.clearAccessPin} onChange={(event) => setValues((current) => ({ ...current, clearAccessPin: event.target.checked }))} />}
                    </FormGrid>
                    <p className="LStudio__savings_security_hint">Para contraseñas bancarias, tokens o códigos 2FA seguí usando la Bóveda personal. Ahorrador guarda datos identificatorios y financieros, no secretos de autenticación.</p>
                </div>
            </div>
        </AppModal>
    );
};
